import { apiClient } from "./client";

export interface SezioneReport {
  chiave: string;
  etichetta: string;
  descrizione: string;
  /** Non si può spegnere — l'intestazione. La UI non ne mostra la casella. */
  sempre?: boolean;
}

/** Un indirizzo a cui un report automatico parte da solo. */
export interface ReportDestinatario {
  id: string;
  email: string;
  nome: string | null;
  attivo: boolean;
  created_at: string;
}

/** L'ultima volta che il job ha girato per questo tipo. */
export interface ReportEsecuzione {
  id: string;
  periodo_da: string;
  periodo_a: string;
  /** `in_corso` | `ok` | `parziale` | `fallito`. */
  stato: string;
  destinatari_ok: number;
  destinatari_ko: number;
  errore: string | null;
  created_at: string;
}

export interface TipoReport {
  id: string;
  /** `cliente` | `device`: il codice sceglie il generatore da questa. */
  chiave: string;
  nome: string;
  descrizione: string | null;
  /** Le sezioni attive, NELL'ORDINE in cui compaiono nel documento. */
  sezioni: string[];
  attivo: boolean;
  updated_at: string;
  updated_by: string | null;
  /**
   * Tutte le sezioni che il codice sa disegnare per questo tipo. Arrivano dal
   * backend e non da una copia qui: sono dichiarate in `report/sezioni.ts`, e
   * un secondo elenco nel frontend divergerebbe alla prima sezione aggiunta.
   */
  sezioni_disponibili: SezioneReport[];
  /**
   * Il report parte da solo, senza che nessuno prema un pulsante. Lo decide il
   * backend (`report/sezioni.ts`), non una copia dell'elenco qui: da questo
   * dipende se la pagina mostra il blocco dei destinatari fissi.
   */
  automatico: boolean;
  /** Vuoto sui tipi manuali: lì i destinatari si scelgono al momento dell'invio. */
  destinatari: ReportDestinatario[];
  ultima_esecuzione: ReportEsecuzione | null;
}

export async function listTipiReport(): Promise<TipoReport[]> {
  const { data } = await apiClient.get<{ tipi: TipoReport[] }>("/tipi-report");
  return data.tipi;
}

export async function updateTipoReport(
  chiave: string,
  payload: { sezioni?: string[]; attivo?: boolean }
): Promise<TipoReport> {
  const { data } = await apiClient.patch<{ tipo: TipoReport }>(`/tipi-report/${chiave}`, payload);
  return data.tipo;
}

// ============================================================================
// DESTINATARI FISSI
// ============================================================================

export async function aggiungiDestinatario(
  chiave: string,
  payload: { email: string; nome?: string }
): Promise<ReportDestinatario> {
  const { data } = await apiClient.post<{ destinatario: ReportDestinatario }>(
    `/tipi-report/${chiave}/destinatari`,
    payload
  );
  return data.destinatario;
}

export async function aggiornaDestinatario(
  chiave: string,
  id: string,
  payload: { attivo?: boolean; nome?: string | null }
): Promise<ReportDestinatario> {
  const { data } = await apiClient.patch<{ destinatario: ReportDestinatario }>(
    `/tipi-report/${chiave}/destinatari/${id}`,
    payload
  );
  return data.destinatario;
}

export async function eliminaDestinatario(chiave: string, id: string): Promise<void> {
  await apiClient.delete(`/tipi-report/${chiave}/destinatari/${id}`);
}

/**
 * Scarica quello che il job manderebbe, senza mandarlo.
 *
 * Un formato per volta: una risposta HTTP porta un file. Passa da `apiClient`
 * come tutti gli altri scarichi, perché serve il token.
 */
export async function generaAdesso(chiave: string, formato: "pdf" | "xlsx", mese?: string): Promise<void> {
  const response = await apiClient.get(`/tipi-report/${chiave}/genera`, {
    params: { formato, mese: mese || undefined },
    responseType: "blob",
  });
  const tipoMime =
    formato === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const url = window.URL.createObjectURL(new Blob([response.data], { type: tipoMime }));
  const a = document.createElement("a");
  a.href = url;
  // Il nome si legge dalla RISPOSTA, non si ricostruisce qui.
  //
  // Col campo mese vuoto il periodo lo sceglie il server (il mese appena
  // chiuso), quindi qui non si sa: ricostruendo il nome usciva
  // `report-mensile.pdf` mentre lo stesso documento spedito per email si chiama
  // `report-mensile-2026-07.pdf`, e chi mette a confronto l'anteprima con quello
  // ricevuto non sa quale mese ha davanti — che e' l'unica cosa per cui
  // l'anteprima esiste.
  //
  // Il browser il `Content-Disposition` di un blob non lo guarda, ma noi si':
  // la risposta e' same-origin (passa dal proxy di Vite), quindi l'intestazione
  // si legge. Il nome costruito resta come ripiego se un giorno non ci fosse.
  const disposizione = String(response.headers["content-disposition"] ?? "");
  const daHeader = /filename="([^"]+)"/.exec(disposizione)?.[1];
  a.download = daHeader || `report-mensile${mese ? `-${mese}` : ""}.${formato}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
